import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { practitionerService } from '../../../services/practitionerService';

const ReferralSettingsTab = ({ practitionerData, onSave }) => {
  const [formData, setFormData] = useState({
    slug: ''
  });
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (practitionerData) {
      setFormData({
        slug: practitionerData?.slug || ''
      });
    }
  }, [practitionerData]);

  const publicUrl = practitionerService?.getPublicUrl(formData?.slug || practitionerData?.slug);
  const qrCodeUrl = practitionerService?.getQRCodeUrl(formData?.slug || practitionerData?.slug);

  const handleInputChange = (e) => {
    const { value } = e?.target;
    // Only allow lowercase letters, numbers, and hyphens
    const sanitized = value?.toLowerCase()?.replace(/[^a-z0-9-]/g, '');
    setFormData({ slug: sanitized });
    setHasChanges(true);
    setError('');
  };

  const handleCopyUrl = async () => {
    try {
      await navigator?.clipboard?.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData?.slug) {
      setError('Le slug est requis');
      return;
    }

    if (formData?.slug?.length < 3) {
      setError('Le slug doit contenir au moins 3 caractères');
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
      setHasChanges(false);
    } catch (err) {
      setError('Erreur lors de la sauvegarde. Ce slug est peut-être déjà utilisé.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* URL Slug Configuration */}
      <div>
        <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
          <Icon name="Link" size={20} className="text-accent" />
          URL personnalisée
        </h3>
        <div className="space-y-4">
          <div className="bg-accent/10 border border-accent/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-primary">À propos de votre URL personnalisée</p>
                <p className="text-sm text-secondary mt-1">
                  Cette URL unique permet aux dentistes d'accéder directement à votre formulaire d'adressage personnalisé.
                  Utilisez uniquement des lettres minuscules, chiffres et tirets.
                </p>
              </div>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-secondary mb-2">
              Identifiant unique (slug)
            </label>
            <div className="flex items-center gap-2">
              <span className="text-muted text-sm">{window?.location?.origin}/p/</span>
              <Input
                name="slug"
                value={formData?.slug}
                onChange={handleInputChange}
                placeholder="mon-cabinet"
                error={error}
                className="flex-1"
                required
              />
            </div>
          </div>
        </div>
      </div>
      {/* Public URL Display */}
      <div>
        <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
          <Icon name="ExternalLink" size={20} className="text-accent" />
          Votre URL publique
        </h3>
        <div className="bg-card-elevated rounded-xl p-6 space-y-4 border border-white/8">
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-card border border-white/12 rounded-lg px-4 py-3">
              <p className="text-sm font-mono text-secondary break-all">{publicUrl}</p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={handleCopyUrl}
              iconName={copied ? 'Check' : 'Copy'}
            >
              {copied ? 'Copié' : 'Copier'}
            </Button>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted">
            <Icon name="Share2" size={16} />
            <span>Partagez cette URL avec vos confrères dentistes</span>
          </div>
        </div>
      </div>
      {/* QR Code */}
      <div>
        <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
          <Icon name="QrCode" size={20} className="text-accent" />
          QR Code
        </h3>
        <div className="bg-card-elevated rounded-xl p-6 border border-white/8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="bg-white p-4 rounded-lg border border-gray-300 shadow-lg">
              <img 
                src={qrCodeUrl} 
                alt="QR Code" 
                className="w-48 h-48"
              />
            </div>
            <div className="flex-1 space-y-3">
              <div className="flex items-start gap-3">
                <Icon name="Smartphone" size={20} className="text-accent mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-primary">Scan rapide</p>
                  <p className="text-sm text-secondary">Les dentistes peuvent scanner ce QR code pour accéder directement à votre formulaire</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="Download" size={20} className="text-accent mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-primary">Téléchargement</p>
                  <p className="text-sm text-secondary">Cliquez droit sur le QR code pour l'enregistrer et l'imprimer</p>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = qrCodeUrl;
                  link.download = `qr-code-${formData?.slug}.png`;
                  link?.click();
                }}
                iconName="Download"
                className="mt-4"
              >
                Télécharger le QR Code
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Marketing Tips */}
      <div>
        <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
          <Icon name="Lightbulb" size={20} className="text-accent" />
          Conseils de diffusion
        </h3>
        <div className="bg-gradient-to-br from-accent/10 to-accent/5 rounded-xl p-6 space-y-3 border border-accent/20">
          <div className="flex items-start gap-3">
            <Icon name="Mail" size={18} className="text-accent mt-0.5" />
            <p className="text-sm text-secondary">Envoyez l'URL par email à vos confrères dentistes</p>
          </div>
          <div className="flex items-start gap-3">
            <Icon name="Printer" size={18} className="text-accent mt-0.5" />
            <p className="text-sm text-secondary">Imprimez le QR code sur vos cartes de visite</p>
          </div>
          <div className="flex items-start gap-3">
            <Icon name="FileText" size={18} className="text-accent mt-0.5" />
            <p className="text-sm text-secondary">Ajoutez le QR code à vos documents de communication</p>
          </div>
          <div className="flex items-start gap-3">
            <Icon name="Globe" size={18} className="text-accent mt-0.5" />
            <p className="text-sm text-secondary">Intégrez l'URL sur votre site web professionnel</p>
          </div>
        </div>
      </div>
      {/* Actions */}
      <div className="flex items-center justify-end gap-4 pt-6 border-t border-white/8">
        <Button
          type="button"
          variant="outline"
          onClick={() => window.location?.reload()}
          disabled={!hasChanges || saving}
        >
          Annuler
        </Button>
        <Button
          type="submit"
          loading={saving}
          disabled={!hasChanges || saving}
          iconName="Save"
        >
          {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
        </Button>
      </div>
    </form>
  );
};

export default ReferralSettingsTab;